'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { LayoutDashboard, FileText, Clock, Tag, BarChart3, LogOut, ChevronRight, Bell, Settings, Menu, Loader2, Users, ExternalLink } from 'lucide-react';
import PendingQueue from './PendingQueue';
import AllThesesTable from './AllThesesTable';
import ElsevierKeywordsAdmin from './ElsevierKeywordsAdmin';
import AdminAnalytics from './AdminAnalytics';
import ReportsExport from './ReportsExport';
import UserManagement from './UserManagement';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';

type AdminTab = 'pending' | 'all-theses' | 'keywords' | 'analytics' | 'reports' | 'users';

export default function AdminPanelShell() {
  const { user, loading, signOut, signInWithMicrosoft, signInWithGoogle } = useAuth();
  const [activeTab, setActiveTab] = useState<AdminTab>('pending');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [userProfile, setUserProfile] = useState<any>(null);
  const [pendingCount, setPendingCount] = useState(0);

  const legacyWebformsUrl = process.env.NEXT_PUBLIC_LEGACY_WEBFORMS_URL || '';
  const isAdminRole = userProfile?.role === 'admin';

  useEffect(() => {
    if (user) {
      loadAdminData();
    }
  }, [user]);

  const loadAdminData = async () => {
    if (!user) return;
    try {
      const supabase = createClient();
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', user.id)
        .maybeSingle();
      setUserProfile(profile);

      const { count } = await supabase
        .from('theses')
        .select('*', { count: 'exact', head: true })
        .in('thesis_status', ['submitted', 'under_review']);
      setPendingCount(count || 0);
    } catch (err) {
      console.log('Load admin data error:', err);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (err) {
      console.log('Sign out error:', err);
    } finally {
      // Always send the user back to the public home page after logout,
      // even if signOut threw, so they're never left on the admin panel.
      window.location.assign('/public-thesis-portal');
    }
  };

  const displayName = userProfile?.full_name || user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'Admin';
  const displayRole = userProfile?.role === 'admin' ? 'Administrator' : userProfile?.role === 'librarian' ? 'Librarian' : 'Librarian / Administrator';
  const initials = displayName.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase();

  const navItems: { id: AdminTab; icon: React.ElementType; label: string; badge?: number; adminOnly?: boolean }[] = [
    { id: 'pending', icon: Clock, label: 'Pending Queue', badge: pendingCount },
    { id: 'all-theses', icon: FileText, label: 'All Theses' },
    { id: 'reports', icon: BarChart3, label: 'Reports & Export' },
    { id: 'keywords', icon: Tag, label: 'Elsevier Keywords' },
    { id: 'analytics', icon: BarChart3, label: 'Analytics' },
    { id: 'users', icon: Users, label: 'User Management', adminOnly: true },
  ];
  const visibleNavItems = navItems.filter((n) => !n.adminOnly || isAdminRole);

  // Not logged in — show OAuth login
  if (!loading && !user) {
    return (
      <div className="min-h-screen bg-[#f7f8fc] flex items-center justify-center" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
        <div className="bg-white border border-[#e2e6f0] rounded-2xl shadow-sm p-8 w-full max-w-md">
          <div className="flex flex-col items-center mb-6">
            <AppLogo size={48} />
            <h1 className="text-xl font-700 text-[#0f1a35] mt-3">Admin Panel</h1>
            <p className="text-sm text-[#4a5578] mt-1 text-center">Sign in with your administrator account</p>
          </div>
          <div className="space-y-3">
            <button
              onClick={() => signInWithMicrosoft('/admin-management-panel')}
              className="w-full flex items-center justify-center gap-3 border border-[#e2e6f0] rounded-xl px-4 py-3 text-sm font-600 text-[#0f1a35] hover:bg-[#f7f8fc] transition-all"
            >
              <svg width="20" height="20" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
                <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
                <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
                <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
              </svg>
              Sign in with Microsoft (Azure AD)
            </button>
            <button
              onClick={() => signInWithGoogle('/admin-management-panel')}
              className="w-full flex items-center justify-center gap-3 border border-[#e2e6f0] rounded-xl px-4 py-3 text-sm font-600 text-[#0f1a35] hover:bg-[#f7f8fc] transition-all"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              Sign in with Google
            </button>
          </div>
          <p className="text-xs text-[#8892a8] text-center mt-4">
            Admin access requires librarian or administrator role
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f7f8fc] flex items-center justify-center" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
        <div className="flex flex-col items-center gap-3">
          <Loader2 size={32} className="animate-spin text-[#1a2e5a]" />
          <p className="text-sm text-[#4a5578]">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-[#f7f8fc]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-56' : 'w-16'} flex-shrink-0 bg-[#1a2e5a] flex flex-col transition-all duration-300 ease-in-out sticky top-0 h-screen z-30`}>
        {/* Logo */}
        <div className="flex items-center gap-3 px-3 py-4 border-b border-white/10 overflow-hidden">
          <div className="flex-shrink-0">
            <AppLogo size={32} />
          </div>
          {sidebarOpen && (
            <div className="min-w-0 overflow-hidden">
              <div className="text-white font-700 text-xs leading-tight truncate">Philips University</div>
              <div className="text-[#c9a84c] text-xs font-500 leading-tight truncate">Admin Panel</div>
            </div>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 px-2 py-4 space-y-1 overflow-hidden">
          {sidebarOpen && (
            <p className="text-white/30 text-xs font-600 uppercase tracking-wider px-3 mb-2">Management</p>
          )}
          {visibleNavItems.map((item) => (
            <button
              key={`admin-nav-${item.id}`}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-500 transition-all duration-150 relative group ${
                activeTab === item.id
                  ? 'bg-white/15 text-white font-600' : 'text-white/60 hover:bg-white/8 hover:text-white'
              }`}
              title={!sidebarOpen ? item.label : undefined}
            >
              <item.icon size={17} className="flex-shrink-0" />
              {sidebarOpen && <span className="truncate">{item.label}</span>}
              {item.badge !== undefined && item.badge > 0 && (
                <span className={`${sidebarOpen ? 'ml-auto' : 'absolute -top-0.5 -right-0.5'} bg-[#c9a84c] text-[#111e3d] text-xs font-700 min-w-[18px] h-[18px] rounded-full flex items-center justify-center px-1`}>
                  {item.badge}
                </span>
              )}
              {!sidebarOpen && (
                <div className="absolute left-full ml-2 px-2 py-1 bg-[#0f1a35] text-white text-xs rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                  {item.label}
                </div>
              )}
            </button>
          ))}

          {sidebarOpen && (
            <>
              <div className="border-t border-white/10 my-3" />
              <p className="text-white/30 text-xs font-600 uppercase tracking-wider px-3 mb-2">Navigation</p>
            </>
          )}
          {!sidebarOpen && <div className="border-t border-white/10 my-3" />}

          <Link
            href="/public-thesis-portal"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-white/50 hover:bg-white/8 hover:text-white transition-all group relative"
            title={!sidebarOpen ? 'Public Repository' : undefined}
          >
            <LayoutDashboard size={17} className="flex-shrink-0" />
            {sidebarOpen && <span className="truncate">Public Repository</span>}
            {!sidebarOpen && (
              <div className="absolute left-full ml-2 px-2 py-1 bg-[#0f1a35] text-white text-xs rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                Public Repository
              </div>
            )}
          </Link>

          {legacyWebformsUrl && (
            <a
              href={legacyWebformsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-white/50 hover:bg-white/8 hover:text-white transition-all group relative"
              title={!sidebarOpen ? 'Legacy Webforms' : undefined}
            >
              <ExternalLink size={17} className="flex-shrink-0" />
              {sidebarOpen && <span className="truncate">Legacy Webforms</span>}
              {!sidebarOpen && (
                <div className="absolute left-full ml-2 px-2 py-1 bg-[#0f1a35] text-white text-xs rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                  Legacy Webforms
                </div>
              )}
            </a>
          )}
        </nav>

        {/* User + collapse */}
        <div className="border-t border-white/10 p-3 space-y-2">
          {sidebarOpen && (
            <div className="flex items-center gap-2.5 px-1 overflow-hidden">
              <div className="w-7 h-7 rounded-full bg-[#c9a84c]/20 border border-[#c9a84c]/40 flex items-center justify-center flex-shrink-0">
                <span className="text-[#c9a84c] text-xs font-700">{initials}</span>
              </div>
              <div className="min-w-0">
                <p className="text-white text-xs font-600 truncate">{displayName}</p>
                <p className="text-white/40 text-xs truncate">{displayRole}</p>
              </div>
            </div>
          )}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="w-full flex items-center justify-center gap-2 text-white/40 hover:text-white py-1.5 rounded-lg hover:bg-white/8 transition-all text-xs"
          >
            {sidebarOpen ? <><ChevronRight size={13} className="rotate-180" /> Collapse</> : <Menu size={14} />}
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Topbar */}
        <header className="bg-white border-b border-[#e2e6f0] sticky top-0 z-20">
          <div className="px-6 xl:px-8 h-14 flex items-center justify-between">
            <div>
              <h1 className="text-base font-700 text-[#0f1a35]">
                {navItems.find(n => n.id === activeTab)?.label || 'Admin Panel'}
              </h1>
              <div className="flex items-center gap-1 text-xs text-[#8892a8]">
                <span>Admin Panel</span>
                <ChevronRight size={10} />
                <span className="text-[#1a2e5a] font-500">{navItems.find(n => n.id === activeTab)?.label}</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button className="relative p-2 text-[#4a5578] hover:text-[#1a2e5a] hover:bg-[#1a2e5a]/5 rounded-lg transition-all">
                <Bell size={16} />
                {pendingCount > 0 && <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 bg-red-500 rounded-full" />}
              </button>
              <button className="p-2 text-[#4a5578] hover:text-[#1a2e5a] hover:bg-[#1a2e5a]/5 rounded-lg transition-all">
                <Settings size={16} />
              </button>
              <div className="h-6 w-px bg-[#e2e6f0]" />
              <button
                onClick={handleSignOut}
                className="flex items-center gap-1.5 text-[#4a5578] hover:text-red-600 text-xs font-500 px-3 py-1.5 border border-[#e2e6f0] hover:border-red-200 rounded-lg transition-all"
              >
                <LogOut size={12} />
                Sign Out
              </button>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 px-6 xl:px-8 2xl:px-10 py-6 overflow-auto">
          {activeTab === 'pending' && <PendingQueue onCountChange={setPendingCount} />}
          {activeTab === 'all-theses' && <AllThesesTable />}
          {activeTab === 'reports' && <ReportsExport />}
          {activeTab === 'keywords' && <ElsevierKeywordsAdmin />}
          {activeTab === 'analytics' && <AdminAnalytics />}
          {activeTab === 'users' && isAdminRole && <UserManagement />}
        </main>
      </div>
    </div>
  );
}